import styled, { css } from 'styled-components';
import { Col, FlexView, Row } from '@cuemath/comps';
import { QM, STATE_RAIL, STATE_WEIGHT } from './constants';

/*
 * Typography lives in these blocks rather than in <Text>, against the
 * house rule, and the reason is the scale rather than convenience.
 *
 * <Text> offers sizes 8/10/13/15/18/22/30/38 and variants at 12/14/16.
 * This board is built on 10px and 11px — eleven of the components below —
 * and one 32px figure. 10 and 13 have slots; 11 and 32 have none, and 11px
 * is the row/caption size the three-pane layout is tuned around. <Text>
 * also has no tabular-figures option, which the counts and elapsed clocks
 * need: a number ticking 9 -> 10 on a poll must not reflow the row it sits
 * in, and the info panel promises exactly that.
 *
 * Converting only the two 13px components would leave the screen half in
 * each system, which is worse than either. Closing this properly means
 * adding an 11px step and a tabular-figures option to <Text>, or widening
 * the board's scale to 12px and accepting a looser layout.
 */
export const Screen = styled(FlexView)`
  min-height: 100vh;
  background: ${QM.BG_PAGE};
  color: ${QM.TEXT_PRIMARY};
  padding: 16px 20px 40px;
  gap: 16px;
`;

export const Card = styled(Col).attrs({ gutter: 0 })`
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_TERTIARY};
  border-radius: 8px;
  box-shadow: ${QM.SHADOW_MD};
`;

export const Bar = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 14px;
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_TERTIARY};
  border-radius: 8px;
`;

export const FieldLabel = styled.span`
  font-size: 10px;
  line-height: 14px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${QM.TEXT_TERTIARY};
`;

export const Segment = styled(Row).attrs({ gutter: 0 })`
  flex: none;
  gap: 2px;
  padding: 2px;
  background: ${QM.BG_WELL};
  border: 1px solid ${QM.BORDER_TERTIARY};
  border-radius: 8px;
`;

const pressable = css`
  font-family: inherit;
  cursor: pointer;
  border: 0;
  background: transparent;
  color: inherit;
`;

export const SegmentButton = styled.button`
  ${pressable};
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${({ $on }) => ($on ? 600 : 400)};
  background: ${({ $on }) => ($on ? QM.BG_CARD : 'transparent')};
  color: ${({ $on }) => ($on ? QM.TEXT_PRIMARY : QM.TEXT_SECONDARY)};
  box-shadow: ${({ $on }) => ($on ? QM.SHADOW_MD : 'none')};

  &:hover {
    color: ${QM.TEXT_PRIMARY};
  }
`;

export const WindowButton = styled.button`
  ${pressable};
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid ${QM.BORDER_SECONDARY};
  border-radius: 8px;
  background: ${QM.BG_CARD};
  font-size: 12px;
  line-height: 16px;
  font-variant-numeric: tabular-nums;

  &:hover {
    border-color: ${QM.BORDER_PRIMARY};
  }
`;

export const Popover = styled(Col).attrs({ gutter: 0 })`
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  min-width: 320px;
  gap: 10px;
  padding: 12px;
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_SECONDARY};
  border-radius: 8px;
  box-shadow: ${QM.SHADOW_LG};
`;

export const Anchored = styled(FlexView)`
  position: relative;
  flex: none;
`;

export const DateTimeInput = styled.input`
  font-family: inherit;
  font-size: 12px;
  padding: 6px 8px;
  color: ${QM.TEXT_PRIMARY};
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_SECONDARY};
  border-radius: 6px;
`;

// --- Poll status -----------------------------------------------------

export const PollChip = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 6px;
  margin-left: auto;
  padding: 5px 10px;
  border-radius: 9999px;
  border: 1px solid ${({ $stopped }) => ($stopped ? QM.BORDER_ERROR : QM.BORDER_TERTIARY)};
  background: ${({ $stopped }) => ($stopped ? QM.BG_ERROR_1 : QM.BG_WELL)};
  font-size: 11px;
  color: ${({ $stopped }) => ($stopped ? QM.TEXT_ERROR : QM.TEXT_SECONDARY)};
  font-variant-numeric: tabular-nums;
`;

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: ${({ $tone }) => $tone};
`;

export const StaleBanner = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: ${QM.BG_ERROR_1};
  border: 1px solid ${QM.BORDER_ERROR};
  border-left: ${STATE_RAIL} solid ${QM.TEXT_ERROR};
  border-radius: 8px;
  font-size: 12px;
  color: ${QM.TEXT_PRIMARY};
`;

export const RetryButton = styled.button`
  ${pressable};
  margin-left: auto;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${QM.TEXT_ON_INVERSE};
  background: ${QM.BG_INVERSE};
`;

/* The window is the one filter that is typed rather than clicked, and a
   `datetime-local` emits a change per segment — so binding it straight to
   the query fired a request per keystroke, several of them for nonsense
   half-typed dates like the year 202612. The inputs edit a draft; this
   commits it. */
export const ApplyButton = styled.button`
  ${pressable};
  margin-left: auto;
  padding: 5px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${QM.TEXT_ON_INVERSE};
  background: ${QM.BG_INVERSE};

  &:disabled {
    cursor: default;
    color: ${QM.TEXT_TERTIARY};
    background: ${QM.BG_WELL};
    border: 1px solid ${QM.BORDER_TERTIARY};
  }
`;

// --- Tiles -----------------------------------------------------------

export const TileRow = styled(Row).attrs({ gutter: 0 })`
  flex: none;
  gap: 12px;
  align-items: stretch;
  flex-wrap: wrap;
`;

const tileBox = css`
  flex: 1 1 170px;
  min-width: 150px;
  text-align: left;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${QM.BG_CARD};
  border: 1px solid ${({ $hot }) => ($hot ? QM.BORDER_ERROR : QM.BORDER_TERTIARY)};
  box-shadow: ${QM.SHADOW_MD};
`;

export const Tile = styled.button`
  ${pressable};
  ${tileBox};

  &:hover {
    border-color: ${({ $hot }) => ($hot ? QM.TEXT_ERROR : QM.BORDER_SECONDARY)};
  }
`;

// The resolved tile holds its own buttons (one per bucket), so its shell
// cannot be a button itself — nesting them is invalid and swallows the
// inner clicks in some browsers.
export const TileShell = styled(Col).attrs({ gutter: 0 })`
  ${tileBox};
`;

export const TileMain = styled.button`
  ${pressable};
  text-align: left;
  background: none;
  border: none;
  padding: 0;
`;

export const TileHead = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  gap: 6px;
  flex: none;
`;

export const TileLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${QM.TEXT_SECONDARY};
`;

export const TileValue = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 32px;
  line-height: 38px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $hot }) => ($hot ? QM.TEXT_ERROR : QM.TEXT_PRIMARY)};
`;

export const TileCaption = styled.span`
  display: block;
  font-size: 11px;
  line-height: 15px;
  color: ${QM.TEXT_TERTIARY};
  font-variant-numeric: tabular-nums;
`;

export const Affordance = styled.span`
  margin-left: auto;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${QM.TEXT_TERTIARY};
`;

export const Chip = styled.button`
  ${pressable};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 9999px;
  font-size: 10px;
  letter-spacing: 0.04em;
  background: ${QM.BG_WELL};
  border: 1px solid ${QM.BORDER_TERTIARY_SOFT};
  color: ${QM.TEXT_SECONDARY};
  font-variant-numeric: tabular-nums;

  &:hover {
    border-color: ${QM.BORDER_SECONDARY};
    color: ${QM.TEXT_PRIMARY};
  }
`;

// --- Panes -----------------------------------------------------------

export const PaneGrid = styled(Row).attrs({ gutter: 0 })`
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

export const Pane = styled(Card)`
  /* The owner column pushes the minimum content width past 400px, so panes
     wrap to two rows before they start clipping their state pills. */
  flex: 1 1 400px;
  min-width: 400px;
  overflow: hidden;
  /* Slow fade out, instant fade in: the ring has to be unmistakable the
     moment the scroll lands, then get out of the way. */
  transition: border-color 600ms ease-out, box-shadow 600ms ease-out;

  ${({ $flash }) =>
    $flash &&
    css`
      border-color: ${QM.TEXT_WARNING};
      box-shadow: 0 0 0 4px ${QM.BORDER_WARNING}, ${QM.SHADOW_LG};
      transition: none;

      /* Tint the head too — the border alone is easy to miss on a wide
         board, and the head is where the eye lands after the scroll. */
      > *:first-child {
        background: ${QM.BG_WARNING_1};
      }
    `}
`;

export const PaneHead = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${QM.BORDER_TERTIARY};
  background: ${QM.BG_WELL};
`;

export const PaneTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
`;

const countFace = css`
  padding: 1px 7px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: ${({ $hot }) => ($hot ? QM.BG_ERROR_HEAVY : QM.BG_CARD)};
  color: ${({ $hot }) => ($hot ? QM.TEXT_ERROR : QM.TEXT_PRIMARY)};
  border: 1px solid ${({ $hot }) => ($hot ? QM.BORDER_ERROR : QM.BORDER_TERTIARY)};
`;

export const Count = styled.span`
  ${countFace};
`;

export const SortNote = styled.span`
  margin-left: auto;
  font-size: 10px;
  color: ${QM.TEXT_TERTIARY};
`;

// Replaces the sort note when the cap is biting. A bare count never reads as
// clickable, and the pane's own scroll gives no hint that rows exist below
// it — so the affordance states the split and carries the click.
export const OpenAll = styled.button`
  ${pressable};
  margin-left: auto;
  font-size: 10px;
  color: ${QM.TEXT_SECONDARY};
  text-decoration: underline;
  text-underline-offset: 2px;
  font-variant-numeric: tabular-nums;

  &:hover {
    color: ${QM.TEXT_PRIMARY};
  }
`;

// Raw `styled.div` on purpose: this is a CSS grid, and the
// `@cuemath/comps` flex primitives cannot express `grid-template-columns`.
export const ColumnHead = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid ${QM.BORDER_TERTIARY};
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${QM.TEXT_TERTIARY};
`;

/* Scrolls sideways rather than clipping. The modal's grid gained two
   columns and its state labels roughly doubled in length, taking the
   min-content width past a 960px card — and `ModalCard` is `overflow:
   hidden`, so the overflow fell off the right edge silently, taking the
   State column with it. Half of a 1440px display is narrow enough to hit
   this. The banded panes carry no grid and so never overflow here. */
export const RowList = styled(Col).attrs({ gutter: 0 })`
  overflow-x: auto;

  /* In a modal the list takes whatever the card has left over, so the pager
     below it is always reachable; in a pane it caps itself. */
  ${({ $fill }) =>
    $fill ? 'flex: 1 1 auto; min-height: 0; max-height: none;' : 'max-height: 520px;'}
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${QM.BG_WELL};
  }
  &::-webkit-scrollbar-thumb {
    background: ${QM.BORDER_SECONDARY};
    border-radius: 9999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${QM.BORDER_PRIMARY};
  }
`;

// Raw `styled.div` on purpose: this is a CSS grid, and the
// `@cuemath/comps` flex primitives cannot express `grid-template-columns`.
export const TicketRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols};
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid ${QM.BORDER_TERTIARY};
  background: ${({ $hot }) => ($hot ? QM.ROW_HOT : 'transparent')};
  font-size: 12px;

  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: ${({ $hot }) => ($hot ? QM.ROW_HOT : QM.BG_WELL)};
  }
`;

export const TicketId = styled.button`
  ${pressable};
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: ${QM.TEXT_PRIMARY};

  &:hover {
    color: ${QM.TEXT_INFO};
  }
`;

export const Muted = styled.span`
  font-size: 11px;
  color: ${QM.TEXT_TERTIARY};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Weight and ink separate an amber reply-owed pill from a quiet row on this
// cream ground; the tints alone are only a few steps apart. The design also
// carried a thick left rail for this, but on a fully-rounded pill it renders
// as a smudge rather than a rail, so the weight carries it alone.
export const StatePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  background: ${({ $tone }) => $tone.bg};
  color: ${({ $tone }) => $tone.ink};
  border: 1px solid ${({ $tone }) => $tone.border};
  font-weight: ${({ $tone }) => ($tone.bold ? 600 : STATE_WEIGHT)};
`;

export const BadgeRow = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  gap: 5px;
  flex-wrap: wrap;
`;

export const Empty = styled(Col).attrs({ gutter: 0, alignItems: 'center' })`
  gap: 4px;
  padding: 34px 16px;
  text-align: center;
`;

// Not `Muted` — that carries `white-space: nowrap` so ticket-row cells
// ellipsize, which here collapses the note onto one overflowing line.
export const EmptyTitle = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${QM.TEXT_SECONDARY};
`;

export const EmptyNote = styled.span`
  display: block;
  /* Centre the capped box itself, not just the text inside it — the
     parent's alignItems does not reach this child. */
  margin-inline: auto;
  max-width: 280px;
  font-size: 11px;
  line-height: 15px;
  color: ${QM.TEXT_TERTIARY};
`;

// --- Modal -----------------------------------------------------------

export const Scrim = styled(FlexView)`
  position: fixed;
  inset: 0;
  z-index: 40;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${QM.SCRIM};
`;

export const ModalCard = styled(Card)`
  /* Declared, not inherited: Col computes to display:block here, so the
     list's flex:1 did nothing and the pager overflowed the card on a short
     viewport — it rendered 600px below the fold. */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: min(960px, 100%);
  max-height: 82vh;
  /* A paged list holds one height across pages, so the last short page does
     not shrink the card out from under the cursor. Capped by max-height, so
     the pager stays on screen on a short viewport. */
  ${({ $paged }) => $paged && 'height: 660px;'}
  box-shadow: ${QM.SHADOW_LG};
`;

export const ModalHead = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid ${QM.BORDER_TERTIARY};
`;

export const CloseButton = styled.button`
  ${pressable};
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${QM.BORDER_SECONDARY};
  font-size: 12px;

  &:hover {
    border-color: ${QM.BORDER_PRIMARY};
  }
`;

export const PagerBar = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 8px;
  padding: 9px 14px;
  border-top: 1px solid ${QM.BORDER_TERTIARY};
  font-size: 11px;
  color: ${QM.TEXT_SECONDARY};
  font-variant-numeric: tabular-nums;
`;

export const PagerButton = styled.button`
  ${pressable};
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${QM.BORDER_SECONDARY};
  font-size: 11px;
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

export const InfoDot = styled.button`
  ${pressable};
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: 1px solid ${QM.BORDER_SECONDARY};
  font-size: 9px;
  line-height: 1;
  color: ${QM.TEXT_TERTIARY};

  &:hover {
    color: ${QM.TEXT_PRIMARY};
    border-color: ${QM.BORDER_PRIMARY};
  }
`;

export const Message = styled(Col).attrs({ gutter: 0, alignItems: 'center' })`
  gap: 6px;
  padding: 60px 20px;
  text-align: center;
  color: ${QM.TEXT_SECONDARY};
`;

export const ScreenTitle = styled.span`
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  color: ${QM.TEXT_PRIMARY};
`;

export const TitleRow = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  flex: none;
  gap: 12px;
  justify-content: space-between;
`;

// A real anchor, not a button: the board stays open in this tab and the
// browser's own open-in-new-tab gestures (middle click, cmd-click) work
// without us reimplementing them.
export const CrossLink = styled.a`
  ${pressable};
  display: inline-block;
  text-decoration: none;
  flex: none;
  padding: 5px 12px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: ${QM.TEXT_SECONDARY};
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_TERTIARY};

  &:hover {
    color: ${QM.TEXT_PRIMARY};
    border-color: ${QM.BORDER_SECONDARY};
  }
`;

// --- Per-tile explainers ---------------------------------------------

// A span, not a button: it lives inside the tile, which is itself a button,
// and nesting those is invalid and swallows the inner click. Carries the
// button role and key handling so it stays reachable from the keyboard.
export const InfoButton = styled.span`
  ${pressable};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 9999px;
  border: 1px solid ${QM.BORDER_SECONDARY};
  color: ${QM.TEXT_SECONDARY};
  font-size: 10px;
  line-height: 14px;
  cursor: help;

  &:hover {
    border-color: ${QM.BORDER_PRIMARY};
    color: ${QM.TEXT_PRIMARY};
  }
`;

export const InfoScrim = styled(FlexView)`
  position: fixed;
  inset: 0;
  z-index: 70;
`;

export const InfoCard = styled(Col).attrs({ gutter: 0 })`
  display: flex;
  flex-direction: column;
  position: fixed;
  width: 324px;
  padding: 13px 14px;
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_SECONDARY};
  border-radius: 8px;
  box-shadow: ${QM.SHADOW_LG};
  overflow-y: auto;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
`;

export const InfoTitle = styled.span`
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
  color: ${QM.TEXT_PRIMARY};
`;

export const InfoBody = styled.span`
  display: block;
  margin-top: 5px;
  font-size: 12px;
  line-height: 17px;
  color: ${QM.TEXT_SECONDARY};
`;

export const InfoLegend = styled(Col).attrs({ gutter: 0 })`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px solid ${QM.BORDER_TERTIARY};
`;

export const InfoLegendRow = styled(Row).attrs({ gutter: 0 })`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const InfoMeaning = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
  color: ${QM.TEXT_SECONDARY};
`;

/* The state moved out of the row and became the band heading, so a pane row
   no longer needs a column for it — which is what freed the width for queue
   and disposition. Rows stack instead: the identity and clock on top, the
   routing underneath, where it reads as context rather than competing with
   the elapsed time. */
export const Band = styled(Col).attrs({ gutter: 0 })``;

export const BandHead = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  gap: 8px;
  padding: 5px 12px;
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${({ $tone }) => $tone.bg};
  border-top: 1px solid ${({ $tone }) => $tone.border};
  border-bottom: 1px solid ${({ $tone }) => $tone.border};
  color: ${({ $tone }) => $tone.ink};
  font-size: 10px;
  font-weight: ${STATE_WEIGHT};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const BandCount = styled.span`
  padding: 0 6px;
  border-radius: 9999px;
  background: ${QM.BG_CARD};
  border: 1px solid ${QM.BORDER_TERTIARY_SOFT};
  color: ${QM.TEXT_SECONDARY};
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;

export const StackedRow = styled(Col).attrs({ gutter: 0 })`
  /* Declared, not inherited: Col computes to display:block here, as
     ModalCard notes, so the gap below is dead weight without it. */
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 7px 12px 7px 9px;
  border-left: ${STATE_RAIL} solid ${({ $tone }) => $tone.border};
  border-bottom: 1px solid ${QM.BORDER_TERTIARY};
  background: ${({ $hot }) => ($hot ? QM.ROW_HOT : 'transparent')};

  &:last-child {
    border-bottom: 0;
  }
  &:hover {
    background: ${({ $hot }) => ($hot ? QM.ROW_HOT : QM.BG_WELL)};
  }
`;

export const StackedTop = styled(Row).attrs({ gutter: 0, alignItems: 'center' })`
  gap: 8px;
  min-width: 0;
  font-size: 12px;

  /* Fixed so the clocks line up down a band. The ids are all 8 characters,
     but the copy glyph swaps to a tick or cross on click and would shunt
     every elapsed time sideways as it did. */
  ${TicketId} {
    width: 92px;
    flex-shrink: 0;
  }
`;

export const Elapsed = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ $tone }) => ($tone.bold ? 600 : 400)};
  color: ${({ $tone }) => $tone.ink};
`;

export const RowMeta = styled.div`
  display: flex;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  font-size: 11px;
  color: ${QM.TEXT_TERTIARY};
`;

/* Source and region are short closed vocabularies, so they never give up
   room. Letting flex shrink every part proportionally truncated all of
   them at once — a row read `CH… · U… · Sharon Mathe… · Tutor o…`. */
export const MetaFixed = styled.span`
  flex-shrink: 0;
`;

export const MetaFlex = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Absorbs almost all of the shrink, because flex divides it in proportion
   to the factor: the disposition is both the longest field and the only one
   with the full value a keystroke away in its tooltip, so a nine-character
   team name should not be losing characters beside it. */
export const MetaDispo = styled(MetaFlex)`
  flex-shrink: 60;
`;

export const MetaSep = styled.span`
  flex-shrink: 0;
  color: ${QM.BORDER_SECONDARY};
`;
